import {defineStore} from "pinia";
import {ReportService} from "@/javascript/services/report.service";
import {OrganizationService} from "@/javascript/services/organization.service";
import {FundService} from "../services/fund.service";
import {CampaignService} from "../services/campaign.service";


export const useOverviewStore = defineStore('overview', {
  state: () => ({
    organizationId: null,
    reportService: new ReportService(),
    organizationService: new OrganizationService(),
    campaignService: null,
    fundService: null,
    intervals: ['Week', 'Month', 'Year'],
    currentInterval: "monthly",
    requestVersion: 0,

    periodDonationSum: 0,
    periodDonationCount: 0,
    donationData: {},
    donationDataDateRange: "",
    donationDataLoaded: false,
    paymentData: {},
    paymentDataLoaded: false,

    topDonors: [],
    topDonorsLoaded: false,

    donationTypeData: {}, // Chart data for one time/ recurring donations
    donationTypeDataLoaded: false,
    donationTypeDataCache: null,

    processorTypeData: {}, // Chart data for online / offline donations
    processorTypeDataLoaded: false,
    processorTypeDataCache: null,

    donationsByFundData: {}, // Chart data for fund breakdown of donations
    donationsByFundDataLoaded: false,
    donationsByFundDataCache: null,

    // New dashboard analytics
    donationFunnelData: {},
    donationFunnelDataLoaded: false,
    donationFunnelDataCache: null,

    websiteTrafficData: {},
    websiteTrafficDataLoaded: false,
    websiteTrafficDataCache: null,

    donorBehaviorData: {},
    donorBehaviorDataLoaded: false,
    donorBehaviorDataCache: null,

    qrAnalyticsData: {},
    qrAnalyticsDataLoaded: false,
    qrAnalyticsDataCache: null,

    recentDonationsData: [],
    recentDonationsDataLoaded: false,

    kpiSummaryData: {},
    kpiSummaryDataLoaded: false,
    kpiSummaryDataCache: null,

    calendarData: {},
    calendarDataLoaded: false,
    calendarYear: new Date().getFullYear(),
    calendarMonth: new Date().getMonth() + 1,

    funds: [],
    fundsLoading: true,
    fundsLoaded: false,
    loading: true,

    campaigns: [],
    campaignsLoading: true,
  }),

  actions: {
    setOrganizationId(id) {
      if (this.organizationId !== id) {
        this.clearData();
      }
      if (id) {
        this.organizationId = id;
        this.campaignService = new CampaignService(id);
        this.fundService = new FundService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    clearData() {
      this.topDonorsLoaded = false;
      this.donationTypeDataLoaded = false;
      this.processorTypeDataLoaded = false;
      this.donationsByFundDataLoaded = false;

      this.periodDonationSum = 0;
      this.periodDonationCount = 0;
      this.donationFunnelDataLoaded = false;
      this.websiteTrafficDataLoaded = false;
      this.donorBehaviorDataLoaded = false;
      this.qrAnalyticsDataLoaded = false;
      this.recentDonationsDataLoaded = false;
      this.kpiSummaryDataLoaded = false;
    },
    loadData() {
      this.loading = true;
      this.campaignsLoading = true;
      this.fundsLoading = true;
      this.campaigns = [];
      this.funds = [];

      this.loadDonationData();
      this.loadPaymentData();
      this.loadDonationTypeData();
      this.loadProcessorTypeData();
      this.loadDonationsByFundData();
      this.loadDonationFunnelData();
      this.loadWebsiteTrafficData();
      this.loadDonorBehaviorData();
      this.loadQrAnalyticsData();
      this.loadRecentDonationsData();
      this.loadKpiSummaryData();
      this.loadCalendarData();
      this.getCampaigns();
      this.getFunds();
      // this.loading = false;
    },

    async loadDataWithIntervals() {
      // Loads the data that is dependent on intervals so we don't download the campaigns and funds repeatedly
      this.requestVersion++;
      this.paymentDataLoaded = false;
      this.topDonorsLoaded = false;
      this.donationTypeDataLoaded = false;
      this.processorTypeDataLoaded = false;
      this.donationsByFundDataLoaded = false;
      this.donationFunnelDataLoaded = false;
      this.websiteTrafficDataLoaded = false;
      this.donorBehaviorDataLoaded = false;
      this.qrAnalyticsDataLoaded = false;

      this.loadDonationData();
      this.loadPaymentData();
      // await this.loadTopDonors();
      this.loadDonationTypeData();
      this.loadProcessorTypeData();
      this.loadDonationsByFundData();
      this.loadDonationFunnelData();
      this.loadWebsiteTrafficData();
      this.loadDonorBehaviorData();
      this.loadQrAnalyticsData();
      this.loadRecentDonationsData();
      this.loadKpiSummaryData();
      this.loadCalendarData();
    },

    loadDonationData() {
      try {
        this.donationDataLoaded = false;
        const version = this.requestVersion;
        this.reportService.organizationDonationProjections(this.organizationId, this.currentInterval)
          .then((data) => {
            if (version !== this.requestVersion) return; // stale, discard
            this.donationData = data;
            this.donationDataDateRange = this.donationData?.date_range;
            this.periodDonationSum = this.donationData?.donations_sum;
            this.periodDonationCount = this.donationData.donations_count;
            this.donationDataLoaded = true;
          });
      } catch (e) {
        console.error('error loading donation data', e.toString());
      }
    },

    loadPaymentData() {
      const version = this.requestVersion;
      try {
        this.reportService.organizationPaymentProjections(this.organizationId, this.currentInterval)
          .then((data) => {
            if (version !== this.requestVersion) return;
            if (data) {
              this.paymentData = data;
              this.paymentDataLoaded = true;
            }
          });
      } catch (e) {
        console.error('error loading payment data', e.toString());
      }
    },


    async loadTopDonors() {
      try {
        this.topDonorsLoaded = false;
        this.topDonors = await this.reportService.organizationTopDonors(this.organizationId, this.currentInterval, 5);
        this.topDonorsLoaded = true;
      } catch (error) {
        console.error('Error loading top donors:', error);
      }
    },

    getFunds() {
      let params = new URLSearchParams();
      params.set('query[organization_id_eq]', this.organizationId);
      params.set('view', 'overview');

      return this.fundService.index(params)
        .then((res) => {
          this.funds = res.data;
        })
        .catch((e) => {
          console.error("Error fetching members", e);
        })
        .finally(() => {
          this.fundsLoading = false;
        });
    },


    loadDonationTypeData() {
      this.donationTypeDataLoaded = false;
      const version = this.requestVersion;

      this.reportService.organizationDonationTypes(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.donationTypeData = chartData;
          this.donationTypeDataCache = _cache || null;
          this.donationTypeDataLoaded = true;
        });
    },

    loadProcessorTypeData() {
      this.processorTypeDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.donationProcessorTypes(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.processorTypeData = chartData;
          this.processorTypeDataCache = _cache || null;
          this.processorTypeDataLoaded = true;
        });
    },

    loadDonationsByFundData() {
      this.donationsByFundDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.donationsPerFund(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.donationsByFundData = chartData;
          this.donationsByFundDataCache = _cache || null;
          this.donationsByFundDataLoaded = true;
        });
    },

    loadDonationFunnelData() {
      this.donationFunnelDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.donationFunnel(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.donationFunnelData = chartData;
          this.donationFunnelDataCache = _cache || null;
          this.donationFunnelDataLoaded = true;
        });
    },

    loadWebsiteTrafficData() {
      this.websiteTrafficDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.websiteTraffic(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.websiteTrafficData = chartData;
          this.websiteTrafficDataCache = _cache || null;
          this.websiteTrafficDataLoaded = true;
        });
    },

    loadDonorBehaviorData() {
      this.donorBehaviorDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.donorBehavior(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.donorBehaviorData = chartData;
          this.donorBehaviorDataCache = _cache || null;
          this.donorBehaviorDataLoaded = true;
        });
    },

    loadQrAnalyticsData() {
      this.qrAnalyticsDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.qrAnalytics(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...chartData } = data || {};
          this.qrAnalyticsData = chartData;
          this.qrAnalyticsDataCache = _cache || null;
          this.qrAnalyticsDataLoaded = true;
        });
    },

    loadRecentDonationsData() {
      this.recentDonationsDataLoaded = false;
      this.reportService.recentDonations(this.organizationId)
        .then((data) => {
          this.recentDonationsData = Array.isArray(data) ? data : [];
          this.recentDonationsDataLoaded = true;
        });
    },

    loadKpiSummaryData() {
      this.kpiSummaryDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.kpiSummary(this.organizationId, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          const { _cache, ...kpiData } = data || {};
          this.kpiSummaryData = kpiData;
          this.kpiSummaryDataCache = _cache || null;
          this.kpiSummaryDataLoaded = true;
        });
    },

    loadCalendarData() {
      this.calendarDataLoaded = false;
      const version = this.requestVersion;
      this.reportService.calendarData(this.organizationId, this.calendarYear, this.calendarMonth, this.currentInterval)
        .then((data) => {
          if (version !== this.requestVersion) return;
          this.calendarData = data || {};
          this.calendarDataLoaded = true;
        });
    },

    navigateCalendar(payload) {
      if (typeof payload === 'object' && payload.jump) {
        this.calendarYear = payload.year;
        this.calendarMonth = payload.month;
      } else {
        this.calendarMonth += payload;
        if (this.calendarMonth > 12) { this.calendarMonth = 1; this.calendarYear++; }
        if (this.calendarMonth < 1) { this.calendarMonth = 12; this.calendarYear--; }
      }
      this.loadCalendarData();
    },

    resetCalendarToToday() {
      this.calendarYear = new Date().getFullYear();
      this.calendarMonth = new Date().getMonth() + 1;
      this.loadCalendarData();
    },

    getCampaigns() {
      let params = new URLSearchParams();
      params.set('query[organization_id_eq]', this.organizationId);
      params.set('view', 'overview');
      this.campaignService.index(params)
        .then((res) => {
          this.campaigns = res.data;
        }).catch((e) => {
        console.error("Error fetching campaigns", e);
      }).finally(() => {
        this.campaignsLoading = false;
      });
    },

    parseChartData(past, projected, labels, priorYear, interval) {
      const projectionDataset = interval === 'yearly'
        ? {
          label: '\xa0' + labels[1] + '\xa0',
          data: projected,
          borderColor: '#3a4473',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          fill: true,
          tension: 0.2,
          borderWidth: 2,
          borderDash: [4, 4],
          pointRadius: 3,
          pointBackgroundColor: 'rgba(0, 189, 255, 0.2)',
          hoverRadius: 6,
          order: 1,
        }
        : {  //Not Yearly
          label: '\xa0' + labels[1] + '\xa0',
          data: projected,
          borderColor: '#3a4473',
          backgroundColor: 'rgba(0, 189, 255, 0.2)',
          fill: true,
          tension: 0.2,
          borderWidth: 2,
          borderDash: [4, 4],
          pointRadius: 3,
          hoverRadius: 6,
        };
      // Green Line
      const datasets = [
        {
          label: '\xa0' + labels[0] + '\xa0',
          data: past,
          borderColor: '#10be5a',
          backgroundColor: 'rgba(16, 190, 90, 0.2)',
          fill: true,
          tension: 0.2,
          borderWidth: 2,
          pointRadius: 3,
          hoverRadius: 6,
          order: 0,
        },
        ];
      if (projected) {
        datasets.push(projectionDataset);
      }

      if (priorYear && Object.values(priorYear).some(v => v !== null)) {
        datasets.push({
          label: '\xa0' + (labels[2] || 'Prior Year') + '\xa0',
          data: priorYear,
          borderColor: '#d1d5db',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.2,
          borderWidth: 1.5,
          borderDash: [6, 3],
          pointRadius: 2,
          hoverRadius: 4,
        });
      }

      return { datasets };
    }
  },

  getters: {
    donationProjectionChartData(state) {
      const donationData = state.donationData['donations'];
      const projectionData = state.donationData['projections'];
      const priorYear = state.donationData['prior_year'];
      const chartData = this.parseChartData(donationData, projectionData, ["Gifts", "Projected Gifts", "Last Year"], priorYear, state.currentInterval);
      return chartData;
    },

    paymentChartData(state) {
      const paymentData = state.paymentData['payments'];
      const projectionData = state.paymentData['projections'];
      const priorYear = state.paymentData['prior_year'];
      const chartData = this.parseChartData(paymentData, projectionData, ["Dollars", "Projected Dollars", "Last Year"], priorYear, state.currentInterval);
      return chartData;
    },
    // Used to check if there is data to project
    hasYearlyProjectionData(state) {
      if (state.currentInterval === 'daily') return true;
      if (state.currentInterval === 'fiscal' || state.currentInterval === 'previous' || state.currentInterval === 'monthly') {
        const donations = state.donationData['donations'];
        return donations && Object.values(donations).some(v => v > 0);
      }
      const projections = state.donationData['projections'];
      return projections !== null
        && projections !== undefined
        && Object.keys(projections).length > 0;
    },
    hasYearlyPaymentProjectionData(state) {
      if (state.currentInterval === 'daily') return true;
      if (state.currentInterval === 'fiscal' || state.currentInterval === 'previous' || state.currentInterval === 'monthly') {
        const payments = state.paymentData['payments'];
        return payments && Object.values(payments).some(v => v > 0);
      }
      const projections = state.paymentData['projections'];
      return projections !== null
        && projections !== undefined
        && Object.keys(projections).length > 0;
    },
  }
});
